
import React, { useEffect, useState } from 'react';
import { Box, Typography, Grid, Card, CardContent, Chip, Button, useTheme } from '@mui/material';
import { getLinkedInTrendingTopics } from '../../utils/linkedinApi';
import CreatePostModal from './CreatePostModal';
import LoadingPage from '../LoadingPage';
import LightbulbIcon from '@mui/icons-material/Lightbulb';

const InspirationPage: React.FC = () => {
    const [topics, setTopics] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedTopic, setSelectedTopic] = useState<string | undefined>(undefined);
    const [modalOpen, setModalOpen] = useState(false);
    const theme = useTheme();

    useEffect(() => {
        const fetchTopics = async () => {
            try {
                const response = await getLinkedInTrendingTopics();
                if (response.ok) {
                    const data = await response.json();
                    setTopics(data.topics || []);
                }
            } catch (error) {
                console.error("Failed to fetch trending topics", error);
            } finally {
                setLoading(false);
            }
        };

        fetchTopics();
    }, []);

    const handleTopicClick = (topicKeyword: string) => {
        setSelectedTopic(topicKeyword);
        setModalOpen(true);
    };

    const handleCreateCustom = () => {
        setSelectedTopic('');
        setModalOpen(true);
    }

    if (loading) return <LoadingPage />;

    return (
        <Box sx={{ p: 3, maxWidth: '1200px', margin: '0 auto' }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
                <Box>
                    <Typography variant="h4" gutterBottom fontWeight="bold">
                        Inspiration
                    </Typography>
                    <Typography variant="subtitle1" color="text.secondary">
                        Discover trending topics on LinkedIn and create engaging content.
                    </Typography>
                </Box>
                <Button variant="contained" startIcon={<LightbulbIcon />} onClick={handleCreateCustom}>
                    Create Post
                </Button>
            </Box>

            <Grid container spacing={3}>
                {topics.map((topic, index) => (
                    <Grid item xs={12} sm={6} md={4} key={index}>
                        <Card
                            sx={{
                                height: '100%',
                                cursor: 'pointer',
                                transition: '0.3s',
                                '&:hover': {
                                    transform: 'translateY(-4px)',
                                    boxShadow: theme.shadows[4]
                                }
                            }}
                            onClick={() => handleTopicClick(topic.keyword)}
                        >
                            <CardContent>
                                <Box display="flex" justifyContent="space-between" alignItems="start" mb={2}>
                                    <Typography variant="h6" fontWeight="bold">
                                        {topic.keyword}
                                    </Typography>
                                    {topic.sentiment && (
                                        <Chip
                                            label={topic.sentiment}
                                            size="small"
                                            color={topic.sentiment === 'Positive' ? 'success' : 'default'}
                                            variant="outlined"
                                        />
                                    )}
                                </Box>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                    {topic.description}
                                </Typography>
                                <Typography variant="caption" color="primary" fontWeight="bold">
                                    Create Post &rarr;
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>
            <CreatePostModal
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                initialTopic={selectedTopic}
            />
        </Box>
    );
};

export default InspirationPage;
